import Combobox from '@/components/common/Combobox';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/common/Form';
import { Input } from '@/components/common/Input';
import { Label } from '@/components/common/Label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/common/Popover';
import { RadioGroup, RadioGroupItem } from '@/components/common/RadioGroup';
import { Textarea } from '@/components/common/Textarea';
import ImageUploadAccount from '@/components/general/ImageUploadAccount';
import { useStore } from '@/hooks/useStore';
import yup from '@/services/yup';
import { FORM_TYPE, FormType, LANGUAGES_SUPPORTED, STATUS } from '@/types/enums';
import { yupResolver } from '@hookform/resolvers/yup';
import { ArrowRight, MapPinIcon } from 'lucide-react';
import { flowResult } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import debounce from 'lodash.debounce';
import { v4 as uuidv4 } from 'uuid';
import { DISPPLAY_MOMENT_YMD_HM, ekDomain, MAX_LENGTH_LINK, ROUTES } from '@/configs/constants';
import { formatDateTime } from '@/utils/datetime';
import { ButtonCus, ButtonCusWithIcon } from '@/components/common/Button';
import { isPathEk } from '@/utils/routes';
import { Checkbox } from '@/components/common/Checkbox';
import GoogleMap from '@/components/general/GoogleMap';
import CustomTimePicker from '@/components/calendar/CustomTimePicker';
import { validateUrl } from '@/utils/validate';

interface BasicInformationProps {
    formType: FormType
};

export default observer(function BasicInformation({ formType }: BasicInformationProps) {
    // hook
    const { t, i18n: { language } } = useTranslation();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const isEk = isPathEk(pathname);
    const { id } = useParams();
    const sessionToken = uuidv4();

    // store
    const {
        locationStore: {
            createLocation, editLocation, deleteLocation, getLocationById, getPricesCombobox,
            location, prices
        },
        regionProvinceStore: {
            regions, provinces,
            getRegions, getProvinces, getProvincesByRegion
        },
        generalStore: {
            getPlaceAutoComplete,
            getLatLngFromPlaceId
        },
        modalStore: {
            showAlertModal, hideModal
        }
    } = useStore();

    // validate schema
    const validateSchema = yup.object().shape({
        priceId: yup
            .string()
            .required('form_error_validate.required'),
        regionId: yup
            .string()
            .required('form_error_validate.required'),
        provinceId: yup
            .string()
            .required('form_error_validate.required'),
        name: yup
            .string()
            .trim()
            .required('form_error_validate.required'),
        address: yup
            .string()
            .trim()
            .required('form_error_validate.required'),
        latitude: yup
            .string()
            .optional()
            .nullable(),
        longitude: yup
            .string()
            .optional()
            .nullable(),
        placeId: yup
            .string(),
        sessionToken: yup
            .string(),
        isShow24h: yup
            .boolean()
            .default(true)
            .optional(),
        openTimeFrom: yup
            .string()
            .nullable(),
        openTimeTo: yup
            .string()
            .nullable(),
        access: yup
            .string()
            .trim(),
        status: yup
            .number(),
        note: yup
            .string()
            .trim(),
        locationImageId: yup
            .string()
            .required('form_error_validate.required'),
        parkingName: yup
            .string()
            .trim(),
        parkingPlaceDetail: yup
            .string()
            .trim(),
        url: yup
            .string()
            .trim()
            .test(
                'validate-url',
                t('server_messages.URL_NOT_FORMAT'),
                (value) => {
                    return validateUrl(value);
                }
            )
            .optional(),
        parkingNote: yup
            .string()
            .trim()
    });

    type FormData = yup.InferType<typeof validateSchema>;

    // state
    const form = useForm<FormData>({
        resolver: yupResolver(validateSchema),
        mode: 'onSubmit',
        reValidateMode: 'onChange',
        defaultValues: {
            priceId: undefined,
            regionId: undefined,
            provinceId: undefined,
            name: '',
            address: '',
            latitude: null,
            longitude: null,
            access: '',
            isShow24h: true,
            openTimeFrom: '06:00',
            openTimeTo: '18:00',
            status: STATUS.Active,
            note: '',
            locationImageId: undefined,
            parkingName: '',
            parkingPlaceDetail: '',
            url: '',
            parkingNote: ''
        }
    });

    const watchIsShow24h = form.watch('isShow24h');
    const watchLatitude = form.watch('latitude');
    const watchLongitude = form.watch('longitude');

    const [imageUrl, setImageUrl] = useState<string>('');
    const [placesSearch, setPlacesSearch] = useState<any[]>([]);
    const [openPopoverSearch, setOpenPopoverSearch] = useState<boolean>(false);

    const positionMarker = useMemo(() => {
        if (!watchLatitude || !watchLongitude) {
            return undefined;
        }

        return {
            lat: Number(watchLatitude),
            lng: Number(watchLongitude)
        };
    }, [watchLatitude, watchLongitude]);

    // lifecycle
    useEffect(() => {
        getPricesCombobox();
        getRegions();
        getProvinces();
    }, []);

    useEffect(() => {
        onFetchDetailLocation(id);
    }, [id]);

    useEffect(() => {
        if (placesSearch?.length > 0) {
            setOpenPopoverSearch(true);
        }
    }, [placesSearch]);

    // fetch data location by id
    const onFetchDetailLocation = async (id: string | undefined) => {
        let locationId: string | undefined = undefined;
        switch (formType) {
            case FORM_TYPE.Create: {
                locationId = location?.id;
                break;
            }
            case FORM_TYPE.Edit: {
                locationId = id;
                break;
            }
        }
        if (locationId) {
            const res = await flowResult(getLocationById(Number(locationId)));
            if (res) {
                const data = res.data;

                setImageUrl(data.attachEntry?.length ? data.attachEntry[0].path : '');
                form.reset({
                    priceId: data?.price?.id?.toString() || '',
                    regionId: data?.region?.id?.toString() || '',
                    provinceId: data?.province?.id?.toString() || '',
                    name: data.name,
                    placeId: data?.placeId || '',
                    address: data.address,
                    latitude: data.latitude,
                    longitude: data.longitude,
                    access: data.access,
                    isShow24h: data.isShow24h,
                    openTimeFrom: data.openTimeFrom || '06:00',
                    openTimeTo: data.openTimeTo || '16:00',
                    status: data.status,
                    note: data.note || '',
                    locationImageId: data.attachEntry?.length ? data.attachEntry[0].id : '',
                    parkingName: data?.parkingName || '',
                    parkingPlaceDetail: data?.parkingPlaceDetail || '',
                    url: data?.url || '',
                    parkingNote: data?.parkingNote || ''
                });

                if (data?.region?.id) {
                    await handleFilterProvince(data.region?.id);
                }
            }
        }
    };

    // filter province when change region
    const handleFilterProvince = async (regionId: number | undefined) => {
        if (regionId) {
            await flowResult(getProvincesByRegion(regionId));
        } else {
            await flowResult(getProvinces());
        }
    };

    const onDebounceGetPlaceAutocomplete = useCallback(
        debounce((addr: string) => {
            onGetPlaceAutocomplete(addr);
        }, 500),
        []
    );

    const onGetPlaceAutocomplete = async (addr: string) => {
        let data = [];
        try {
            if (addr.trim()) {
                const response = await flowResult(getPlaceAutoComplete({ input: addr.trim(), sessionToken }));
                data = response;
            }
        } catch (error) {
        } finally {
            setPlacesSearch(data);
        }
    };

    const onSubmit = async (data: FormData) => {
        let dataSubmit: any = { ...data };

        dataSubmit.placeId = data.placeId || '';
        dataSubmit.latitude = data.latitude || null;
        dataSubmit.longitude = data.longitude || null;
        dataSubmit.sessionToken = sessionToken || '';
        dataSubmit.priceId = convertNumberValue(data.priceId);
        dataSubmit.regionId = convertNumberValue(data.regionId);
        dataSubmit.provinceId = convertNumberValue(data.provinceId);
        dataSubmit.status = data?.status?.toString() === '1' ? STATUS.Active : STATUS.Inactive;
        dataSubmit.locationImageId = convertNumberValue(data.locationImageId);

        if (data.isShow24h) {
            dataSubmit.openTimeFrom = '';
            dataSubmit.openTimeTo = '';
        }

        switch (formType) {
            case FORM_TYPE.Create: {
                if (location?.id) {
                    const resEdit = await flowResult(editLocation(location?.id as string, dataSubmit));
                    if (resEdit?.ok) {
                        showAlertModal({
                            id: 'update-location-success',
                            type: 'success',
                            content: t('messages.update_success')
                        });
                    }
                } else {
                    const resCreate = await flowResult(createLocation(dataSubmit));
                    if (resCreate?.ok) {
                        showAlertModal({
                            id: 'add-location-success',
                            type: 'success',
                            content: t('messages.add_success')
                        });
                    }
                }
                break;
            }
            case FORM_TYPE.Edit: {
                const res = await flowResult(editLocation(id as string, dataSubmit));
                if (res?.ok) {
                    showAlertModal({
                        id: 'update-location-success',
                        type: 'success',
                        content: t('messages.update_success')
                    });
                }
                break;
            }
        }
    };

    // convert to number
    const convertNumberValue = (value: any): number | null => {
        if (typeof value === 'boolean') {
            return value ? 1 : 0;
        }

        if (!value) return null;

        if (typeof value !== 'number') {
            const valueTmp = Number(value);
            return !isNaN(valueTmp) ? valueTmp : null;
        }

        return null;
    };

    // handle click btn delete location
    const onDeleteLocation = () => {
        showAlertModal({
            id: 'delete-location-confirm',
            isConfirmModal: true,
            content: t('messages.confirm_delete'),
            onSave: () => {
                onDeleteLocationById(Number(id));
                hideModal('delete-location-confirm');
            }
        });
    };

    // delete location by id
    const onDeleteLocationById = async (locationId: number) => {
        const res = await flowResult(deleteLocation(locationId));
        if (res?.ok) {
            showAlertModal({
                id: 'delete-location-success',
                type: 'success',
                content: t('messages.delete_success'),
                onClickXIcon: () => navigate(isEk ? `/${ekDomain}/${ROUTES.locationManagement.hrefEK}` : ROUTES.locationManagement.href)
            });
        }
    };

    const onChangePosition = (value: any) => {
        const { lat, lng } = value;
        lat && form.setValue('latitude', lat);
        lng && form.setValue('longitude', lng);
    };

    return (
        <div>
            <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)}>
                    <div className='layout-card'>
                        <h2 className='title-2 mb-6'>{t('words_title.basic_info')}</h2>
                        <div className='grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 md:gap-6'>
                            <FormField
                                control={form.control}
                                name='regionId'
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel required>{t('field.region')}</FormLabel>
                                        <FormControl>
                                            <Combobox
                                                value={field.value ?? ''}
                                                onChange={(item) => {
                                                    field.onChange(item?.value);
                                                    form.setValue('regionId', item?.code);
                                                    form.trigger('regionId');
                                                    form.setValue('provinceId', '');
                                                    handleFilterProvince(item?.code);
                                                }}
                                                items={
                                                    (Array.isArray(regions) && regions?.length > 0 ?
                                                        [
                                                            ...regions
                                                                .filter(item => item.id && item.name)
                                                                .map(item => ({
                                                                    label: language === LANGUAGES_SUPPORTED.Japanese ? item.nameJP : item.name,
                                                                    value: item.id?.toString(),
                                                                    code: item.id
                                                                }))
                                                        ] :
                                                        []
                                                    )
                                                }
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name='provinceId'
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel required>{t('field.province')}</FormLabel>
                                        <FormControl>
                                            <Combobox
                                                value={field.value ?? ''}
                                                onChange={(item) => {
                                                    field.onChange(item?.value);
                                                    form.setValue('provinceId', item?.code);
                                                    form.trigger('provinceId');
                                                }}
                                                items={
                                                    (Array.isArray(provinces) && provinces?.length > 0 ?
                                                        [
                                                            ...provinces
                                                                .filter(item => item.id && item.name)
                                                                .map(item => ({
                                                                    label: language === LANGUAGES_SUPPORTED.Japanese ? item.nameJP : item.name,
                                                                    value: item.id?.toString(),
                                                                    code: item.id
                                                                }))
                                                        ] :
                                                        []
                                                    )
                                                }
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name='name'
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel required>{t('field.location_name')}</FormLabel>
                                        <FormControl>
                                            <Input
                                                type='text'
                                                placeholder={t('placeholder.enter')}
                                                {...field}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name='priceId'
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel required>{t('field.pricing')}</FormLabel>
                                        <FormControl>
                                            <Combobox
                                                search={false}
                                                value={field.value ?? ''}
                                                onChange={(item) => {
                                                    field.onChange(item?.value);
                                                    form.setValue('priceId', item?.code);
                                                    form.trigger('priceId');
                                                }}
                                                items={
                                                    (Array.isArray(prices) && prices?.length > 0 ?
                                                        [
                                                            ...prices
                                                                .filter(item => item.id && item.name)
                                                                .map(item => ({
                                                                    label: item.name,
                                                                    value: item.id?.toString(),
                                                                    code: item.id
                                                                }))
                                                        ] :
                                                        []
                                                    )
                                                }
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name='address'
                                render={({ field }) => (
                                    <FormItem className='col-span-full'>
                                        <FormLabel required>{t('field.address')}</FormLabel>
                                        <Popover open={openPopoverSearch}>
                                            <PopoverTrigger className='w-full'>
                                                <FormControl>
                                                    <Input
                                                        value={field.value}
                                                        placeholder={t('placeholder.enter')}
                                                        onChange={(e) => {
                                                            field.onChange(e.target.value);
                                                            onDebounceGetPlaceAutocomplete(e.target.value);
                                                        }}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter') {
                                                                e.preventDefault();
                                                            }
                                                        }}
                                                    />
                                                </FormControl>
                                            </PopoverTrigger>
                                            <PopoverContent
                                                className='w-full p-1 !max-w-[900px]'
                                                align='start'
                                                autoFocus={false}
                                                widthFull={true}
                                                onOpenAutoFocus={e => e.preventDefault()}
                                                onPointerDownOutside={() => setOpenPopoverSearch(false)}
                                                avoidCollisions={false}
                                                side='bottom'
                                                sideOffset={4}
                                            >
                                                {
                                                    placesSearch?.length > 0 && (
                                                        <div className='divide-y divide-gray-200'>
                                                            {
                                                                placesSearch.map((place, index) => (
                                                                    <div
                                                                        key={index}
                                                                        className='py-2 px-1 text-sm flex items-center gap-2 hover:cursor-pointer hover:bg-slate-200'
                                                                        onClick={async () => {
                                                                            const res = await flowResult(getLatLngFromPlaceId({
                                                                                sessionToken,
                                                                                placeId: place.placeId
                                                                            }));

                                                                            if (res?.location) {
                                                                                const { latitude, longitude } = res?.location;
                                                                                form.setValue('latitude', latitude);
                                                                                form.setValue('longitude', longitude);
                                                                            }

                                                                            if (place?.address) {
                                                                                form.setValue('address', place.address);
                                                                            }
                                                                            form.setValue('placeId', place.placeId);
                                                                            setOpenPopoverSearch(false);
                                                                        }}
                                                                    >
                                                                        <div>
                                                                            <MapPinIcon className='w-4 h-4' />
                                                                        </div>
                                                                        <div>{place?.address}</div>
                                                                    </div>
                                                                ))
                                                            }
                                                        </div>
                                                    )
                                                }
                                            </PopoverContent>
                                        </Popover>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <div className='col-span-full sm:col-span-1 xl:col-span-2 h-full min-h-[224px] flex flex-col'>
                                <Label required className='mb-2'>{t('field.map_location')}</Label>
                                <div className='flex-1'>
                                    <GoogleMap positionInput={positionMarker} onChangePosition={onChangePosition} />
                                </div>
                            </div>

                            <div className='col-span-full sm:col-span-1 xl:col-span-2'>
                                <FormLabel required>{t('field.location_image')}</FormLabel>
                                <ImageUploadAccount
                                    form={form}
                                    filedName='locationImageId'
                                    required={true}
                                    urlImagePreview={imageUrl}
                                    className='h-56'
                                />
                            </div>

                            <div className='col-span-full grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-6'>
                                <FormField
                                    control={form.control}
                                    name='access'
                                    render={({ field }) => (
                                        <FormItem className='col-span-full'>
                                            <FormLabel>{t('field.access')}</FormLabel>
                                            <FormControl>
                                                <Input
                                                    type='text'
                                                    placeholder={t('placeholder.enter')}
                                                    {...field}
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </div>

                            <div className='col-span-full'>
                                <FormLabel>{t('field.open_time')}</FormLabel>
                                <div className='flex flex-col gap-4'>
                                    <FormField
                                        control={form.control}
                                        name='isShow24h'
                                        render={({ field }) => (
                                            <FormItem className='flex items-center h-8'>
                                                <FormControl>
                                                    <Checkbox
                                                        checked={field.value}
                                                        onCheckedChange={field.onChange}
                                                    />
                                                </FormControl>
                                                <FormLabel className='font-normal ml-1.5 mb-0 cursor-pointer'>
                                                    {t('field.24_hours')}
                                                </FormLabel>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                    {watchIsShow24h !== true && (
                                        <div className='flex justify-between items-center w-full sm:w-2/3'>
                                            <FormField
                                                control={form.control}
                                                name='openTimeFrom'
                                                render={({ field }) => (
                                                    <FormItem className='w-[45%]'>
                                                        <FormControl>
                                                            <CustomTimePicker
                                                                value={field.value}
                                                                onChange={field.onChange}
                                                            />
                                                        </FormControl>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />
                                            <div className='h-full max-h-12 flex items-center'>
                                                <div className='size-[30px] rounded-full border border-border-outline flex justify-center items-center p-1'>
                                                    <ArrowRight />
                                                </div>
                                            </div>
                                            <FormField
                                                control={form.control}
                                                name='openTimeTo'
                                                render={({ field }) => (
                                                    <FormItem className='w-[45%]'>
                                                        <FormControl>
                                                            <CustomTimePicker
                                                                value={field.value}
                                                                onChange={field.onChange}
                                                            />
                                                        </FormControl>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>

                            <FormField
                                control={form.control}
                                name='note'
                                render={({ field }) => (
                                    <FormItem className='col-span-full'>
                                        <FormLabel>{t('field.note')}</FormLabel>
                                        <FormControl>
                                            <Textarea
                                                placeholder={t('placeholder.enter')}
                                                className='min-h-28'
                                                {...field}
                                            />
                                        </FormControl>
                                        <FormMessage className='mt-1' />
                                    </FormItem>
                                )}
                            />
                        </div>

                        <div className='mt-6'>
                            <h2 className='title-2 mb-6'>利用詳細メール記載事項</h2>
                            <div className='grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 md:gap-6'>
                                <FormField
                                    control={form.control}
                                    name='parkingName'
                                    render={({ field }) => (
                                        <FormItem className='col-span-1'>
                                            <FormLabel>{t('field.location_cms.parking_name')}</FormLabel>
                                            <FormControl>
                                                <Input
                                                    type='text'
                                                    placeholder={t('placeholder.enter')}
                                                    {...field}
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={form.control}
                                    name='parkingPlaceDetail'
                                    render={({ field }) => (
                                        <FormItem className='col-span-1'>
                                            <FormLabel>{t('field.location_cms.parking_place_detail')}</FormLabel>
                                            <FormControl>
                                                <Input
                                                    type='text'
                                                    placeholder={t('placeholder.enter')}
                                                    {...field}
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={form.control}
                                    name='url'
                                    render={({ field }) => (
                                        <FormItem className='col-span-1'>
                                            <FormLabel>{t('field.location_cms.url')}</FormLabel>
                                            <FormControl>
                                                <Input
                                                    type='text'
                                                    placeholder={t('placeholder.enter')}
                                                    maxLength={MAX_LENGTH_LINK}
                                                    isHalfWidth
                                                    {...field}
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={form.control}
                                    name='parkingNote'
                                    render={({ field }) => (
                                        <FormItem className='col-span-1'>
                                            <FormLabel>{t('field.location_cms.parking_note')}</FormLabel>
                                            <FormControl>
                                                <Input
                                                    type='text'
                                                    placeholder={t('placeholder.enter')}
                                                    {...field}
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={form.control}
                                    name='status'
                                    render={({ field }) => (
                                        <FormItem className='md:flex md:items-center col-span-full'>
                                            <FormLabel className='mb-2 md:mb-0 min-w-32 md:min-w-40' required>{t('field.status')}</FormLabel>
                                            <FormControl>
                                                <RadioGroup
                                                    key={field.value}
                                                    onValueChange={(value) => {
                                                        field.onChange(value);
                                                    }}
                                                    value={field.value?.toString()}
                                                    className='flex gap-6 w-2/3'
                                                >
                                                    <div className='flex items-center space-x-2'>
                                                        <RadioGroupItem value={STATUS.Active.toString()} id='active' />
                                                        <Label htmlFor='active' className='font-normal'>{t('field.active')}</Label>
                                                    </div>
                                                    <div className='flex items-center space-x-2'>
                                                        <RadioGroupItem value={STATUS.Inactive.toString()} id='inactive' />
                                                        <Label htmlFor='inactive' className='font-normal'>{t('field.inactive')}</Label>
                                                    </div>
                                                </RadioGroup>
                                            </FormControl>
                                        </FormItem>
                                    )}
                                />

                                {formType === FORM_TYPE.Edit && (
                                    <>
                                        <div className='cool-span-full'>
                                            <Label>{t('field.create_by')}</Label>
                                            <div>

                                                {location?.createdBy || '--'}
                                            </div>
                                        </div>
                                        <div className='col-span-full'>
                                            <Label>{t('field.update_time')}</Label>
                                            <div>
                                                {location?.updatedAt ? formatDateTime(location?.updatedAt, DISPPLAY_MOMENT_YMD_HM, { useLocalTime: false }) : '--'}
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>

                    </div>

                    <div className='mt-6 flex justify-center gap-4'>
                        {formType === FORM_TYPE.Edit ?
                            (
                                <>
                                    <ButtonCusWithIcon
                                        type='button'
                                        variant='outline'
                                        size='sm'
                                        iconId='#icon-delete'
                                        onClick={onDeleteLocation}
                                    >
                                        {t('button.delete')}
                                    </ButtonCusWithIcon>
                                    <ButtonCusWithIcon
                                        type='submit'
                                        variant='default'
                                        size='sm'
                                        iconId='#icon-update'
                                    >
                                        {t('button.update')}
                                    </ButtonCusWithIcon>
                                </>
                            ) :
                            (
                                <ButtonCus
                                    variant='default'
                                    size='sm'
                                    type='submit'
                                >
                                    {t('button.add')}
                                </ButtonCus>
                            )}
                    </div>

                </form>
            </Form>
        </div>
    );
});
